import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PlanItemDTO, TimelineEntry } from '@clear/contracts';
import {
  effective,
  GATES,
  isQualityUnlocked,
  maxClearable,
  pendingSince,
  readyQty,
  type Gate,
} from '@clear/domain';
import { formatNumber, type Translate } from '@clear/i18n';
import { Button, ConfirmButton, Icon, NumberPad, NumberValue, Sheet, type IconName } from '@clear/ui';
import type { Lookups } from '../data/bootstrap';
import { useI18n } from '../i18n';
import { api } from '../lib/api';
import { age, hhmm } from '../lib/time';
import { useGateActions, usePlannerActions } from './actions';
import { gateLabelFor, ItemDots, ModelMeta, StatusChip, Thumb } from './item';

function ModelTitle({ item, lk }: { item: PlanItemDTO; lk: Lookups }) {
  const m = lk.model(item.modelId);
  return (
    <>
      {m.name} {m.finish}
    </>
  );
}

/** Short / Not available / Hold → one tap on a reason chip saves (2 taps total). */
export function ReasonSheet({
  item,
  gate,
  lk,
  mode = 'blocked',
  onClose,
}: {
  item: PlanItemDTO;
  gate: Gate;
  lk: Lookups;
  mode?: 'blocked' | 'observation';
  onClose: () => void;
}) {
  const { t, lang } = useI18n();
  const actions = useGateActions(gate);
  const reasons = lk.reasonsFor(gate);
  const label = mode === 'observation' ? t('gateStatus.observation') : t(`gateStatus.blocked.${gate}`);
  return (
    <Sheet
      title={mode === 'observation' ? label : t('floor.whyTitle', { label })}
      subtitle={
        <>
          <ModelTitle item={item} lk={lk} /> · {formatNumber(item.qty)} {t('common.units')}
        </>
      }
      media={<Thumb modelId={item.modelId} lk={lk} size={44} />}
      onClose={onClose}
      closeLabel={t('common.close')}
    >
      <div className="reasons">
        {reasons.map((r) => (
          <button
            key={r.id}
            type="button"
            className="reason"
            onClick={() => {
              const ok = mode === 'observation' ? actions.ok(item, r.id) : actions.blocked(item, r.id);
              if (ok) onClose();
            }}
          >
            <span className="reason__icon">
              <Icon name={r.icon as IconName} />
            </span>
            {lang === 'hi' ? r.labelHi : r.labelEn}
          </button>
        ))}
      </div>
      <p className="muted" style={{ textAlign: 'center', fontSize: 13 }}>
        {t('floor.oneTapSaves')}
      </p>
    </Sheet>
  );
}

/** Partial → key in the OK qty → Save (3 taps + digits). Quality also picks a reason. */
export function PartialSheet({
  item,
  gate,
  lk,
  onClose,
}: {
  item: PlanItemDTO;
  gate: Gate;
  lk: Lookups;
  onClose: () => void;
}) {
  const { t, lang } = useI18n();
  const actions = useGateActions(gate);
  const [val, setVal] = useState('');
  const [reasonId, setReasonId] = useState<string | null>(null);
  const max = maxClearable(item, gate);
  const v = Number(val) || 0;
  const tooBig = v >= max && val !== '';
  const needReason = gate === 'quality';
  const canSave = v > 0 && v < max && (!needReason || !!reasonId);
  const save = () => {
    if (canSave && actions.partial(item, v, reasonId)) onClose();
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) setVal((s) => (s + e.key).replace(/^0+/, '').slice(0, 5));
      else if (e.key === 'Backspace') setVal((s) => s.slice(0, -1));
      else if (e.key === 'Enter') save();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  const label = t(`gateStatus.partial.${gate}`);
  return (
    <Sheet
      title={t('floor.partialTitle', { label })}
      subtitle={<ModelTitle item={item} lk={lk} />}
      media={<Thumb modelId={item.modelId} lk={lk} size={44} />}
      onClose={onClose}
      closeLabel={t('common.close')}
    >
      <NumberValue value={val} suffix={`${t('common.of')} ${formatNumber(max)}`} />
      <p className={`np-hint${tooBig ? ' np-hint--err' : ''}`}>
        {tooBig
          ? t('floor.mustBeLess', { max: formatNumber(max) })
          : v > 0
            ? t(gate === 'quality' ? 'floor.splitHeld' : 'floor.splitComing', {
                ok: formatNumber(v),
                rest: formatNumber(max - v),
              })
            : ' '}
      </p>
      <NumberPad value={val} onChange={setVal} deleteLabel={t('a11y.delete')} />
      {needReason && (
        <>
          <p className="eyebrow">{t('floor.reasonForHeld')}</p>
          <div className="reasons">
            {lk.reasonsFor(gate).map((r) => (
              <button
                key={r.id}
                type="button"
                className="reason"
                aria-pressed={reasonId === r.id}
                onClick={() => setReasonId(r.id)}
              >
                <span className="reason__icon">
                  <Icon name={r.icon as IconName} />
                </span>
                {lang === 'hi' ? r.labelHi : r.labelEn}
              </button>
            ))}
          </div>
        </>
      )}
      <Button variant="primary" size="lg" block disabled={!canSave} onClick={save}>
        {t('common.save')}
      </Button>
    </Sheet>
  );
}

/** Planner: change a line's quantity with the number pad. */
export function QtySheet({ item, lk, onClose }: { item: PlanItemDTO; lk: Lookups; onClose: () => void }) {
  const { t } = useI18n();
  const actions = usePlannerActions();
  const [val, setVal] = useState('');
  const v = Number(val) || 0;
  return (
    <Sheet
      title={t('planner.newQty')}
      subtitle={
        <>
          <ModelTitle item={item} lk={lk} /> · {t('planner.nowQty', { n: formatNumber(item.qty) })}
        </>
      }
      media={<Thumb modelId={item.modelId} lk={lk} size={44} />}
      onClose={onClose}
      closeLabel={t('common.close')}
    >
      <NumberValue value={val} suffix={t('common.units')} />
      <p className="np-hint">
        {v > item.qty ? t('planner.qtyUpHint') : v > 0 && v < item.qty ? t('planner.qtyDownHint') : ' '}
      </p>
      <NumberPad value={val} onChange={setVal} quick={[100, 500, 1000]} deleteLabel={t('a11y.delete')} />
      <Button
        variant="primary"
        size="lg"
        block
        disabled={v <= 0 || v === item.qty}
        onClick={() => {
          actions.setQty(item, v);
          onClose();
        }}
      >
        {t('common.save')}
      </Button>
    </Sheet>
  );
}

function timelineText(e: TimelineEntry, item: PlanItemDTO, t: Translate, lk: Lookups): string {
  if (e.kind === 'event') {
    const p = (e.payload ?? {}) as Record<string, unknown>;
    const params: Record<string, string> = {};
    if (e.type === 'item.qty') {
      params.from = formatNumber(Number(p.from));
      params.to = formatNumber(Number(p.to));
    }
    if (e.type === 'item.priority') params.to = t(`priority.${String(p.to) as 'high'}`);
    const key = `timeline.${e.type}` as Parameters<Translate>[0];
    return t(key, params);
  }
  const g = e.gate!;
  const gate = t(`gates.${g}`);
  const reason = lk.reasonLabel(e.reasonId);
  if (e.status === 'ok')
    return reason ? t('timeline.gateObservation', { reason }) : t('timeline.gateOk', { gate });
  if (e.status === 'partial') {
    return g === 'quality'
      ? t('timeline.gateQPartial', { ok: formatNumber(e.okQty ?? 0), reason })
      : t('timeline.gatePartial', { gate, ok: formatNumber(e.okQty ?? 0), qty: formatNumber(item.qty) });
  }
  return t('timeline.gateBlocked', { gate, label: t(`gateStatus.blocked.${g}`), reason });
}

/** Tap any line → why it is stuck, who to call, and its full history (1 tap, PRD). */
export function ItemSheet({
  item,
  lk,
  canEdit,
  onClose,
}: {
  item: PlanItemDTO;
  lk: Lookups;
  canEdit: boolean;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const actions = usePlannerActions();
  const [qtyOpen, setQtyOpen] = useState(false);
  const tl = useQuery({
    queryKey: ['timeline', item.id, JSON.stringify(item.gates), item.qty, item.priority, item.startedAt],
    queryFn: () => api<{ entries: TimelineEntry[] }>(`/items/${item.id}/timeline`),
    staleTime: 5_000,
  });
  const m = lk.model(item.modelId);
  if (qtyOpen) return <QtySheet item={item} lk={lk} onClose={() => setQtyOpen(false)} />;

  const gateRow = (g: Gate) => {
    const e = item.gates[g];
    const name = t(`gates.${g}`);
    const owner = lk.owner(g);
    let l1: string;
    let l2: string;
    if (e) {
      l1 = `${name}: ${gateLabelFor(t, g, e.status)}`;
      l2 = [
        t('timeline.cleared', { n: formatNumber(effective(item, g)) }),
        lk.reasonLabel(e.reasonId),
        `${hhmm(e.at, lk.tz)} ${t('common.by', { name: lk.userName(e.userId) })}`,
      ]
        .filter(Boolean)
        .join(' · ');
    } else if (g === 'quality' && !isQualityUnlocked(item)) {
      l1 = `${name}: ${t('gateStatus.locked')}`;
      l2 = t('timeline.lockedHint');
    } else {
      l1 = `${name}: ${t('gateStatus.pending')}`;
      l2 = t('timeline.waitingOwner', {
        age: age(pendingSince(item, g)),
        name: owner?.name ?? '—',
        ext: owner?.ext ?? '—',
      });
    }
    const phone = (e ? lk.user(e.userId) : owner)?.mobile;
    return (
      <div className="gate-row" key={g}>
        <ItemDots item={{ ...item, gates: e ? { [g]: e } : {} }} lk={lk} small />
        <div>
          <b>{l1}</b>
          <span>{l2}</span>
        </div>
        {phone && (
          <a
            className="btn btn--line btn--sm"
            href={`tel:${phone}`}
            aria-label={`${t('common.call')} ${owner?.name ?? ''}`}
          >
            <Icon name="phone" />
          </a>
        )}
      </div>
    );
  };

  return (
    <Sheet
      wide
      side
      history={false}
      title={`${m.name} · ${formatNumber(item.qty)}`}
      subtitle={<ModelMeta modelId={item.modelId} lk={lk} />}
      media={<Thumb modelId={item.modelId} lk={lk} size={48} />}
      onClose={onClose}
      closeLabel={t('common.close')}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <StatusChip item={item} />
        <span className="muted" style={{ fontSize: 14 }}>
          {t('timeline.readyOf', {
            r: formatNumber(readyQty(item)),
            q: formatNumber(item.qty),
            p: t(`priority.${item.priority}`),
          })}
        </span>
      </div>
      <div className="gatebox">{GATES.map(gateRow)}</div>
      <p className="eyebrow">{t('timeline.title')}</p>
      <div className="timeline">
        {(tl.data?.entries ?? [])
          .slice()
          .reverse()
          .map((e, i) => (
            <div key={i} className={`tl${e.undoneAt ? ' tl--undone' : ''}`}>
              <span className="tl__time">{hhmm(e.at, lk.tz)}</span>
              {timelineText(e, item, t, lk)}
              <span className="muted"> · {lk.userName(e.userId)}</span>
              {e.undoneAt ? <span className="muted"> ({t('timeline.undone')})</span> : null}
            </div>
          ))}
        {tl.isPending && <div className="tl muted">{t('common.loading')}</div>}
      </div>
      {canEdit && !item.cancelled && item.startedAt == null && (
        <div className="form-actions">
          <Button onClick={() => setQtyOpen(true)}>{t('planner.changeQty')}</Button>
          <ConfirmButton
            variant="danger"
            armedVariant="danger"
            label={t('planner.cancelLine')}
            confirmLabel={t('planner.tapAgainCancel')}
            onConfirm={() => {
              actions.cancel(item);
              onClose();
            }}
          />
        </div>
      )}
    </Sheet>
  );
}
